/**
 * Client for the living-survey read surfaces: the public anonymized aggregate +
 * Ask the Room (page 2), and the admin-gated rollup + raw responses (page 3).
 */

import { accessHeaders } from "./survey-api";

const apiBase = (): string => import.meta.env.VITE_API_BASE_URL || "";

export type InitiativeTally = {
  key: string;
  label: string;
  isCustom: boolean;
  involvement: { directlyInvolved: number; aware: number; notAware: number };
  medals: { gold: number; silver: number; bronze: number; didntLand: number };
  rated: number;
  score: number;
};

export type SurveyStats = { total: number; completed: number };

export async function fetchAggregate(): Promise<{ ok: boolean; stats?: SurveyStats; tallies?: InitiativeTally[] }> {
  try {
    const res = await fetch(`${apiBase()}/api/survey/aggregate`, { headers: { ...accessHeaders() } });
    if (!res.ok) return { ok: false };
    return (await res.json()) as { ok: boolean; stats: SurveyStats; tallies: InitiativeTally[] };
  } catch {
    return { ok: false };
  }
}

export type AskResult =
  | { ok: true; answer: string; threadId: string; lightInput?: boolean; stats: SurveyStats; tallies: InitiativeTally[] }
  | { ok: true; budgetExceeded: true; stats: SurveyStats; tallies: InitiativeTally[] }
  | { ok: false; error: string; stats?: SurveyStats; tallies?: InitiativeTally[] };

/** Ask the room. Pass threadId to continue an existing anonymous conversation. */
export async function askRoom(question: string, threadId?: string | null): Promise<AskResult> {
  try {
    const res = await fetch(`${apiBase()}/api/survey/ask`, {
      method: "POST",
      headers: { "content-type": "application/json", ...accessHeaders() },
      body: JSON.stringify({ question, threadId: threadId ?? null }),
    });
    const body = await res.json();
    if (!res.ok) return { ok: false, error: body?.error ?? `http_${res.status}`, stats: body?.stats, tallies: body?.tallies };
    return body as AskResult;
  } catch {
    return { ok: false, error: "network" };
  }
}

export type RawResponse = {
  id: string;
  respondentName: string | null;
  roleBand: string | null;
  respondentLocation: string | null;
  respondentDept: string | null;
  status: string;
  finalMdBlobUrl: string | null;
  completedAt: string | null;
  createdAt: string | null;
  answers: { questionKey: string; questionText: string; answerText: string; isFollowup: boolean; orderIndex: number }[];
  initiatives: { initiativeKey: string; label: string; isCustom: boolean; involvement: string | null; medal: string | null; followupText: string | null; orderIndex: number }[];
};

export type RoomQuestion = { question: string; count: number };

export async function fetchAdminSurvey(): Promise<
  | { ok: true; stats: SurveyStats; tallies: InitiativeTally[]; responses: RawResponse[]; questions: RoomQuestion[] }
  | { ok: false; status: number }
> {
  try {
    const res = await fetch(`${apiBase()}/api/admin/survey`, { credentials: "include" });
    if (!res.ok) return { ok: false, status: res.status };
    return (await res.json()) as {
      ok: true;
      stats: SurveyStats;
      tallies: InitiativeTally[];
      responses: RawResponse[];
      questions: RoomQuestion[];
    };
  } catch {
    return { ok: false, status: 0 };
  }
}
